import React, { useEffect, useState } from "react";
import {
    Shield, Check, AlertTriangle, Smartphone, KeyRound, RefreshCw, Copy,
    Download, Trash2, X,
} from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

/**
 * SecuritySettings — per-user TOTP/2FA enrollment.
 *
 * Stages:
 *   1. status === false                → "Enable 2FA" CTA
 *   2. setup returned                  → show QR + secret + code input
 *   3. status === true                 → "2FA active" card + disable flow
 */
export default function SecuritySettings({ fullWidth = false }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [status, setStatus] = useState(null);  // {enabled, enabled_at}
    const [setup, setSetup] = useState(null);     // {qr_png_base64, secret, provisioning_uri}
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [disablePw, setDisablePw] = useState("");
    const [disableCode, setDisableCode] = useState("");
    const [showDisable, setShowDisable] = useState(false);

    const refresh = async () => {
        try {
            const { data } = await http.get("/auth/totp/status");
            setStatus(data);
        } catch (e) { toast.error(errMsg(e)); }
    };
    useEffect(() => { refresh(); }, []);

    const startSetup = async () => {
        setBusy(true);
        try {
            const r = await http.post("/auth/totp/setup");
            setSetup(r.data);
            setCode("");
        } catch (e) { toast.error(errMsg(e)); }
        finally { setBusy(false); }
    };

    const enable = async () => {
        if (code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
        setBusy(true);
        try {
            await http.post("/auth/totp/enable", { code });
            toast.success("2FA enabled — you'll be prompted on next login");
            setSetup(null);
            setCode("");
            await refresh();
        } catch (e) { toast.error(errMsg(e)); }
        finally { setBusy(false); }
    };

    const disable = async () => {
        setBusy(true);
        try {
            await http.post("/auth/totp/disable", { password: disablePw, code: disableCode });
            toast.success("2FA disabled");
            setShowDisable(false);
            setDisablePw("");
            setDisableCode("");
            await refresh();
        } catch (e) { toast.error(errMsg(e)); }
        finally { setBusy(false); }
    };

    const downloadExport = async () => {
        try {
            const { data } = await http.get("/me/export");
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 10);
            a.href = url;
            a.download = `moovbytes-export-${user?.email || "me"}-${stamp}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("Export downloaded.");
        } catch (e) { toast.error(errMsg(e)); }
    };

    const [recoveryStatus, setRecoveryStatus] = useState(null);
    const [recoveryCodes, setRecoveryCodes] = useState(null);
    const [recoveryBusy, setRecoveryBusy] = useState(false);

    const refreshRecovery = async () => {
        try {
            const { data } = await http.get("/auth/totp/recovery-codes/status");
            setRecoveryStatus(data);
        } catch { /* non-fatal — card just shows a loading dash */ }
    };
    useEffect(() => { if (status?.enabled) refreshRecovery(); }, [status?.enabled]);

    const generateRecoveryCodes = async () => {
        setRecoveryBusy(true);
        try {
            const { data } = await http.post("/auth/totp/recovery-codes/generate");
            setRecoveryCodes(data.codes);
            await refreshRecovery();
        } catch (e) { toast.error(errMsg(e)); }
        finally { setRecoveryBusy(false); }
    };

    const [showDelete, setShowDelete] = useState(false);
    const [deletePw, setDeletePw] = useState("");
    const [deletePhrase, setDeletePhrase] = useState("");
    const deleteMe = async () => {
        setBusy(true);
        try {
            await http.delete("/me", { data: { password: deletePw, confirm_phrase: deletePhrase } });
            toast.success("Account deleted.");
            await logout();
            navigate("/login", { replace: true });
        } catch (e) {
            toast.error(errMsg(e));
        } finally { setBusy(false); }
    };

    if (!status) return <div className="p-6 text-sm text-muted-foreground">Loading security settings…</div>;

    return (
        <div className={`mx-auto space-y-5 p-6 ${fullWidth ? "max-w-none" : "max-w-3xl"}`} data-testid="security-settings-page">
            <header>
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                    Account security
                </div>
                <h1 className="mt-1 font-display text-4xl font-bold tracking-tight">2FA &amp; sign-in</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                    Add a Time-based One-Time Password (TOTP) as a second factor on top of your password.
                    Works with Authy, 1Password, Google Authenticator, Microsoft Authenticator, etc.
                </p>
            </header>

            <div className="rounded-xl border border-border bg-surface p-5">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-[#1D4ED8]/20 to-[#1E40AF]/20 text-[#1D4ED8]">
                        <Shield size={18} />
                    </div>
                    <div className="flex-1">
                        <h2 className="font-display text-base font-bold leading-tight">Two-factor authentication</h2>
                        <p className="text-[11px] text-muted-foreground">
                            {status.enabled
                                ? `Active since ${(status.enabled_at || "").slice(0, 10)}`
                                : "Currently off — enable to harden your account."}
                        </p>
                    </div>
                    {status.enabled ? (
                        <span className="mb-pill border-[#10b981]/40 bg-[#10b981]/10 text-[#10b981]" data-testid="totp-status-on">
                            <Check size={11} /> Active
                        </span>
                    ) : (
                        <span className="mb-pill border-amber-400/40 bg-amber-500/10 text-amber-300" data-testid="totp-status-off">
                            <AlertTriangle size={11} /> Off
                        </span>
                    )}
                </div>

                {/* Enrollment flow */}
                {!status.enabled && !setup && (
                    <div className="mt-5 flex justify-end">
                        <button onClick={startSetup} disabled={busy} data-testid="totp-start-setup"
                                className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-[#1D4ED8] to-[#1E40AF] px-4 py-2 text-sm font-semibold text-white shadow hover:brightness-110 disabled:opacity-50">
                            <Smartphone size={13} /> {busy ? "Generating…" : "Enable 2FA"}
                        </button>
                    </div>
                )}

                {!status.enabled && setup && (
                    <div className="mt-5 space-y-4" data-testid="totp-enroll-pane">
                        <ol className="space-y-3 text-sm">
                            <li className="rounded-md border border-border bg-background p-3">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">1. Scan this QR with your authenticator app</div>
                                <div className="mt-2 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
                                    <img
                                        src={setup.qr_png_base64}
                                        alt="TOTP QR code"
                                        className="h-44 w-44 rounded-md border border-border bg-white p-2"
                                        data-testid="totp-qr"
                                    />
                                    <div className="space-y-2 text-xs">
                                        <div className="text-muted-foreground">Can't scan? Type this secret into the app:</div>
                                        <div className="flex items-center gap-1.5">
                                            <code className="rounded bg-muted px-2 py-1 font-mono text-[11px]" data-testid="totp-secret">{setup.secret}</code>
                                            <button onClick={() => { navigator.clipboard.writeText(setup.secret); toast.success("Secret copied"); }}
                                                    data-testid="totp-secret-copy"
                                                    className="rounded-md border border-border p-1.5 text-muted-foreground hover:border-foreground/40 hover:text-foreground">
                                                <Copy size={11} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </li>
                            <li className="rounded-md border border-border bg-background p-3">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">2. Enter the 6-digit code your app shows</div>
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <input
                                        value={code}
                                        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                        inputMode="numeric"
                                        placeholder="123456"
                                        data-testid="totp-code-input"
                                        className="w-32 rounded-md border border-input bg-background px-3 py-2 text-center font-mono text-lg tracking-[0.3em] focus:border-[#1D4ED8] focus:outline-none focus:ring-1 focus:ring-[#1D4ED8]/30"
                                    />
                                    <button onClick={enable} disabled={busy || code.length !== 6}
                                            data-testid="totp-enable-btn"
                                            className="inline-flex items-center gap-2 rounded-md bg-foreground px-4 py-2 text-sm font-semibold text-background hover:opacity-90 disabled:opacity-50">
                                        <Check size={13} /> {busy ? "Verifying…" : "Activate 2FA"}
                                    </button>
                                    <button onClick={startSetup} disabled={busy}
                                            data-testid="totp-regenerate"
                                            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs text-muted-foreground hover:border-foreground/40 hover:text-foreground">
                                        <RefreshCw size={11} /> Regenerate
                                    </button>
                                </div>
                            </li>
                        </ol>
                    </div>
                )}

                {/* Disable flow */}
                {status.enabled && !showDisable && (
                    <div className="mt-5 flex justify-end">
                        <button onClick={() => setShowDisable(true)} data-testid="totp-show-disable"
                                className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:border-[hsl(var(--destructive))] hover:text-[hsl(var(--destructive))]">
                            Disable 2FA
                        </button>
                    </div>
                )}
                {status.enabled && showDisable && (
                    <div className="mt-5 space-y-3 rounded-md border border-amber-400/40 bg-amber-500/[0.04] p-3" data-testid="totp-disable-pane">
                        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-amber-300">
                            <AlertTriangle size={11} /> Confirm both password AND a fresh 2FA code to disable
                        </div>
                        <input
                            type="password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)}
                            placeholder="Your current password"
                            data-testid="totp-disable-password"
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-[#1D4ED8] focus:outline-none focus:ring-1 focus:ring-[#1D4ED8]/30"
                        />
                        <input
                            value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            inputMode="numeric" placeholder="123456"
                            data-testid="totp-disable-code"
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-center font-mono text-base tracking-[0.3em] focus:border-[#1D4ED8] focus:outline-none focus:ring-1 focus:ring-[#1D4ED8]/30"
                        />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => { setShowDisable(false); setDisablePw(""); setDisableCode(""); }}
                                    className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold uppercase tracking-widest hover:border-foreground/40">
                                Cancel
                            </button>
                            <button onClick={disable} disabled={busy || !disablePw || disableCode.length !== 6}
                                    data-testid="totp-disable-btn"
                                    className="inline-flex items-center gap-1.5 rounded-md bg-[hsl(var(--destructive))] px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-white hover:opacity-90 disabled:opacity-50">
                                <KeyRound size={11} /> Disable 2FA
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Recovery codes — only meaningful once 2FA is actually on;
                a device-loss backup for the TOTP flow above. */}
            {status.enabled && (
                <div className="rounded-xl border border-border bg-surface p-5" data-testid="recovery-codes-section">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#EEF4FF] text-[#1D4ED8] dark:bg-brand/10">
                            <KeyRound size={18} />
                        </div>
                        <div className="flex-1">
                            <h2 className="font-display text-base font-bold leading-tight">Recovery codes</h2>
                            <p className="text-[11px] text-muted-foreground">
                                {recoveryStatus == null
                                    ? "Loading…"
                                    : recoveryStatus.generated
                                        ? `${recoveryStatus.count_remaining} of ${recoveryStatus.count_total} codes remaining`
                                        : "No recovery codes generated yet."}
                            </p>
                        </div>
                        <button
                            onClick={generateRecoveryCodes}
                            disabled={recoveryBusy}
                            data-testid="recovery-codes-generate-btn"
                            aria-label={recoveryStatus?.generated ? "Regenerate recovery codes" : "Generate recovery codes"}
                            className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-xs font-semibold hover:bg-muted/40 disabled:opacity-50"
                        >
                            <RefreshCw size={12} /> {recoveryBusy ? "Generating…" : recoveryStatus?.generated ? "Regenerate" : "Generate codes"}
                        </button>
                    </div>

                    {recoveryCodes && (
                        <div className="mt-4 rounded-lg border border-amber-400/40 bg-amber-500/[0.06] p-4" data-testid="recovery-codes-reveal">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-600 dark:text-amber-300">
                                    <AlertTriangle size={12} /> Copy these codes now — they won't be shown again.
                                </div>
                                <button onClick={() => setRecoveryCodes(null)} aria-label="Dismiss recovery codes" className="text-muted-foreground hover:text-foreground">
                                    <X size={14} />
                                </button>
                            </div>
                            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
                                {recoveryCodes.map((c) => (
                                    <code key={c} className="rounded bg-background px-2 py-1.5 text-center font-mono text-xs">{c}</code>
                                ))}
                            </div>
                            <button
                                onClick={() => { navigator.clipboard.writeText(recoveryCodes.join("\n")); toast.success("Recovery codes copied"); }}
                                data-testid="recovery-codes-copy-all-btn"
                                className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:border-foreground/40"
                            >
                                <Copy size={12} /> Copy all
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* ── Privacy / GDPR section ──────────────────────────────────
                Subject Access Request (export) + Right-to-Erasure (delete).
                Live on the same page as 2FA so anything user-controlled is
                in one place. Backend endpoints: GET /me/export, DELETE /me.
            */}
            <div className="rounded-xl border border-border bg-surface p-5" data-testid="privacy-section">
                <div className="flex items-center gap-2">
                    <Shield size={16} className="text-[#00C2FF]" />
                    <h2 className="font-display text-lg font-semibold">Privacy &amp; data rights</h2>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                    Download every record we hold about you, or close your account permanently.
                </p>

                <div className="mt-4 grid gap-3 md:grid-cols-2">
                    {/* Export */}
                    <div className="rounded-lg border border-border bg-background/40 p-4">
                        <div className="flex items-center gap-2">
                            <Download size={14} className="text-[#1D4ED8]" />
                            <div className="font-display text-sm font-semibold">Export my data</div>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                            Generates a JSON file with your profile, jobs you created, schedules,
                            audit log entries, API tokens, share links, and reports. Stored locally
                            on your machine.
                        </p>
                        <button
                            type="button"
                            onClick={downloadExport}
                            data-testid="privacy-export-btn"
                            className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-[#1D4ED8]/40 bg-[#1D4ED8]/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#1D4ED8] hover:bg-[#1D4ED8]/15"
                        >
                            <Download size={12} /> Download export
                        </button>
                    </div>

                    {/* Delete */}
                    <div className="rounded-lg border border-[hsl(var(--destructive))]/30 bg-[hsl(var(--destructive))]/[0.04] p-4">
                        <div className="flex items-center gap-2">
                            <Trash2 size={14} className="text-[hsl(var(--destructive))]" />
                            <div className="font-display text-sm font-semibold text-[hsl(var(--destructive))]">Delete my account</div>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                            Soft-delete: your identity fields are anonymised, password &amp; TOTP
                            invalidated, and API tokens revoked. Audit logs retain a pseudonym
                            reference for SOC2 compliance. Sole-admins must hand off ownership first.
                        </p>
                        {!showDelete ? (
                            <button
                                type="button"
                                onClick={() => setShowDelete(true)}
                                data-testid="privacy-delete-open-btn"
                                className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/15"
                            >
                                <Trash2 size={12} /> Begin deletion
                            </button>
                        ) : (
                            <div className="mt-3 space-y-2">
                                <input
                                    type="password" placeholder="Confirm your password"
                                    value={deletePw} onChange={(e) => setDeletePw(e.target.value)}
                                    data-testid="privacy-delete-password"
                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-[hsl(var(--destructive))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--destructive))]/30"
                                />
                                <input
                                    type="text" placeholder='Type "delete my account"'
                                    value={deletePhrase} onChange={(e) => setDeletePhrase(e.target.value)}
                                    data-testid="privacy-delete-phrase"
                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-[hsl(var(--destructive))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--destructive))]/30"
                                />
                                <div className="flex justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={() => { setShowDelete(false); setDeletePw(""); setDeletePhrase(""); }}
                                        className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold uppercase tracking-widest hover:border-foreground/40"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={deleteMe}
                                        disabled={busy || !deletePw || deletePhrase.trim().toLowerCase() !== "delete my account"}
                                        data-testid="privacy-delete-confirm-btn"
                                        className="inline-flex items-center gap-1.5 rounded-md bg-[hsl(var(--destructive))] px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-white hover:opacity-90 disabled:opacity-50"
                                    >
                                        <Trash2 size={11} /> Permanently delete
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
